import React from "react";
import { Alert, Modal, Spin, Tag } from "antd";
import { useProfiles } from "../../hooks/api/useProfile";
import { PatternBackground } from "../../components/PatternBackground";
import ResponsiveActionBar from "../../components/ResponsiveActionBar";
import ResponsiveTableWrapper from "../../components/ResponsiveTableWrapper";
import { ModalDialog } from "../../components/ModalDialog";
import { notifyUser } from "../../util/util";
import {
  useContributionRates,
  useUpsertContributionRates,
} from "../../hooks/api/useContributionRates";

export const ContributionRatesPage = () => {
  const { data: profiles, isLoading } = useProfiles();
  const activeProfile = profiles?.find((p) => p?.active) || profiles?.[0];
  const isActiveTier3 = String(activeProfile?.tier) === "3";

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const pendingSubmitRef = React.useRef(null);
  const [ratePairsForm, setRatePairsForm] = React.useState([
    {
      name: "",
      employerContributionRate: "",
      employeeContributionRate: "",
      primary: true,
    },
  ]);

  const { data: ratesResponse, isLoading: isRatesLoading } =
    useContributionRates({
      enabled: Boolean(isActiveTier3),
    });
  const upsertMutation = useUpsertContributionRates();

  const serverRatePairs = React.useMemo(() => {
    return ratesResponse?.data?.ratePairs || ratesResponse?.ratePairs || [];
  }, [ratesResponse]);

  React.useEffect(() => {
    if (!isActiveTier3) return;
    if (!serverRatePairs || serverRatePairs.length === 0) return;

    setRatePairsForm(
      serverRatePairs.map((p) => ({
        id: p.id,
        name: p.name ?? "",
        employerContributionRate: p.employerContributionRate ?? "",
        employeeContributionRate: p.employeeContributionRate ?? "",
        primary: Boolean(p.primary),
      })),
    );
  }, [isActiveTier3, serverRatePairs]);

  if (!isLoading && !isActiveTier3) {
    return (
      <div className="container-fluid">
        <Alert
          type="info"
          showIcon
          message="Contribution Rates"
          description="This page is only available for Tier 3 employers. Switch to a Tier 3 profile to view it."
        />
      </div>
    );
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 220,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Employer (%)",
      dataIndex: "employerContributionRate",
      key: "employerContributionRate",
      width: 140,
      render: (v) => <span>{v ?? "-"}</span>,
    },
    {
      title: "Employee (%)",
      dataIndex: "employeeContributionRate",
      key: "employeeContributionRate",
      width: 140,
      render: (v) => <span>{v ?? "-"}</span>,
    },
    {
      title: "Total (%)",
      key: "totalContributionRate",
      width: 140,
      render: (_, record) => {
        const employer = Number(record?.employerContributionRate);
        const employee = Number(record?.employeeContributionRate);
        if (!Number.isFinite(employer) || !Number.isFinite(employee))
          return <span>-</span>;
        return <span>{employer + employee}</span>;
      },
    },
    {
      title: "Primary",
      dataIndex: "primary",
      key: "primary",
      width: 120,
      render: (v) => (v ? <Tag color="green">PRIMARY</Tag> : <Tag>—</Tag>),
    },
  ];

  const toggleModal = () => {
    if (isModalOpen && (upsertMutation.isLoading || upsertMutation.isPending)) {
      upsertMutation.reset();
    }
    setIsModalOpen((s) => !s);
  };

  const setRowField = (index, field, value) => {
    setRatePairsForm((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addRow = () => {
    setRatePairsForm((prev) => [
      ...prev,
      {
        name: "",
        employerContributionRate: "",
        employeeContributionRate: "",
        primary: false,
      },
    ]);
  };

  const removeRow = (index) => {
    setRatePairsForm((prev) => prev.filter((_, i) => i !== index));
  };

  const setPrimary = (index) => {
    setRatePairsForm((prev) =>
      prev.map((row, i) => ({
        ...row,
        primary: i === index,
      })),
    );
  };

  const submitRates = () => {
    if (!ratePairsForm || ratePairsForm.length === 0) {
      return notifyUser(
        "error",
        "Please add at least one rate pair",
        "Validation Error",
      );
    }

    const cleaned = ratePairsForm.map((p) => ({
      name: String(p.name || "").trim(),
      employerContributionRate: Number(p.employerContributionRate),
      employeeContributionRate: Number(p.employeeContributionRate),
      primary: Boolean(p.primary),
    }));

    const invalidRow = cleaned.find(
      (p) =>
        !p.name ||
        Number.isNaN(p.employerContributionRate) ||
        Number.isNaN(p.employeeContributionRate),
    );
    if (invalidRow) {
      return notifyUser(
        "error",
        "Please fill all fields with valid numbers",
        "Validation Error",
      );
    }

    const outOfRangeRow = cleaned.find(
      (p) =>
        p.employerContributionRate < 0 ||
        p.employerContributionRate > 100 ||
        p.employeeContributionRate < 0 ||
        p.employeeContributionRate > 100,
    );
    if (outOfRangeRow) {
      return notifyUser(
        "error",
        "Contribution rates must be between 0 and 100",
        "Validation Error",
      );
    }

    const exceedsTotalRow = cleaned.find(
      (p) => p.employerContributionRate + p.employeeContributionRate > 100,
    );
    if (exceedsTotalRow) {
      const remaining = Math.max(
        0,
        100 - exceedsTotalRow.employerContributionRate,
      );
      return notifyUser(
        "error",
        `Employer + Employee rates must not exceed 100. With employer at ${exceedsTotalRow.employerContributionRate}, employee must be ${remaining} or less.`,
        "Validation Error",
      );
    }

    const totals = cleaned.map(
      (p) => p.employerContributionRate + p.employeeContributionRate,
    );
    const totalsSet = new Set(totals);
    if (totalsSet.size !== totals.length) {
      return notifyUser(
        "error",
        "Each plan’s total (Employer + Employee) must be unique.",
        "Validation Error",
      );
    }

    if (!cleaned.some((p) => p.primary)) {
      return notifyUser(
        "error",
        "Please select a primary plan",
        "Validation Error",
      );
    }

    pendingSubmitRef.current = cleaned;
    setIsConfirmOpen(true);
  };

  return (
    <div className="">
      <PatternBackground>
        <p className="lead fw-bold">Contribution Rates</p>
        <small>Manage Tier 3 contribution rates</small>
      </PatternBackground>

      <div className="om-container">
        <div className="p-4 shadow-sm bg-white rounded">
          <ResponsiveActionBar
            title=""
            onExportCSV={null}
            onExportPDF={null}
            exportLoading={false}
            extraActions={
              !serverRatePairs?.length ? (
                <button
                  className="btn btn-primary"
                  onClick={toggleModal}
                  disabled={isRatesLoading}
                >
                  ADD RATES <i className="bi bi-plus-circle" />
                </button>
              ) : null
            }
          />

          <Spin spinning={isRatesLoading}>
            <ResponsiveTableWrapper
              columns={columns}
              dataSource={serverRatePairs}
              rowKey={(record, index) => record?.id || record?.name || index}
              pagination={{
                current: 1,
                pageSize: 10,
                total: serverRatePairs?.length || 0,
                hideOnSinglePage: true,
              }}
              loading={isRatesLoading}
              onChange={() => {}}
            />
          </Spin>
        </div>
      </div>

      <ModalDialog
        isOpen={isModalOpen}
        title="Manage Contribution Rates"
        closeFn={toggleModal}
      >
        <Spin spinning={upsertMutation.isLoading || upsertMutation.isPending}>
          {(() => {
            const disableAddRow =
              upsertMutation.isLoading || upsertMutation.isPending;
            return (
              <div
                className="d-flex justify-content-end mb-3"
                style={{ gap: 10, alignItems: "center" }}
              >
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={addRow}
                  disabled={disableAddRow}
                >
                  Add Row <i className="bi bi-plus-circle" />
                </button>
              </div>
            );
          })()}

          {ratePairsForm.map((row, idx) => (
            <div
              key={row.id || idx}
              className="mb-4 p-3"
              style={{ border: "1px solid #eee", borderRadius: 8 }}
            >
              <div className="mb-3">
                <omk-text-field
                  label="Name"
                  value={row.name}
                  name="name"
                  onInput={(e) => setRowField(idx, "name", e.target.value)}
                  type="text"
                  className="mb-2"
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <omk-text-field
                    label="Employer Contribution Rate (%)"
                    value={row.employerContributionRate}
                    name="employerContributionRate"
                    onInput={(e) =>
                      setRowField(
                        idx,
                        "employerContributionRate",
                        e.target.value,
                      )
                    }
                    type="number"
                    step="0.01"
                    className="mb-2"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <omk-text-field
                    label="Employee Contribution Rate (%)"
                    value={row.employeeContributionRate}
                    name="employeeContributionRate"
                    onInput={(e) =>
                      setRowField(
                        idx,
                        "employeeContributionRate",
                        e.target.value,
                      )
                    }
                    type="number"
                    step="0.01"
                    className="mb-2"
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <omk-radio
                    label="Primary"
                    value={String(idx)}
                    checked={Boolean(row.primary)}
                    onClick={() => setPrimary(idx)}
                  ></omk-radio>
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="btn btn-danger"
                    type="button"
                    onClick={() => removeRow(idx)}
                    disabled={ratePairsForm.length === 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}

          <button
            className="btn btn-primary mt-2"
            onClick={submitRates}
            disabled={upsertMutation.isLoading || upsertMutation.isPending}
          >
            {upsertMutation.isLoading || upsertMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              <>
                SAVE RATES <i className="bi bi-check2-circle" />
              </>
            )}
          </button>
        </Spin>
      </ModalDialog>

      <Modal
        open={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        footer={null}
        centered
        closable={false}
        width={560}
      >
        <div className="p-5" style={{ textAlign: "center" }}>
          <i
            className="bi bi-exclamation-triangle-fill text-warning"
            style={{ fontSize: "4rem" }}
          />
          <p className="lead mt-4">Confirm submission</p>
          <p className="text-black-50 mb-0">
            Are you sure you want to save these changes? Once saved, this action
            cannot be reversed and can only be updated by contacting customer
            support.
          </p>

          <div className="d-flex justify-content-center gap-3 mt-4">
            <button
              className="btn btn-white"
              type="button"
              onClick={() => setIsConfirmOpen(false)}
              disabled={upsertMutation.isLoading || upsertMutation.isPending}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                const cleaned = pendingSubmitRef.current;
                if (!cleaned) return;
                upsertMutation.mutate(
                  { ratePairs: cleaned },
                  {
                    onSuccess: () => {
                      setIsConfirmOpen(false);
                      setIsModalOpen(false);
                    },
                    onError: () => setIsConfirmOpen(false),
                  },
                );
              }}
              disabled={upsertMutation.isLoading || upsertMutation.isPending}
            >
              Proceed
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
